"use client";

import { useState, useEffect } from "react";
import { Info } from "@phosphor-icons/react";
import { truncateDecimals } from "@/app/utils/format-number";
import Link from "next/link";

enum Interior {
  NONE,
  BASIC,
  PREMIUM,
  LUXURY,
  // ULTRA_LUXURY,
}

const interiorCosts = {
  [Interior.NONE]: 0,
  [Interior.BASIC]: 1000,
  [Interior.PREMIUM]: 1600,
  [Interior.LUXURY]: 2700,
  // [Interior.ULTRA_LUXURY]: 5000,
};

const formatCurrency = (value: number) => {
  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(value);
};

const numberToCroresLakhs = (value: number) => {
  let crore = Math.floor(value / 10000000);
  let lakh = Math.floor((value % 10000000) / 100000);
  let remaining = value % 100000;
  let result = "₹";

  if (crore > 0) {
    result += `${crore} crore `;
  }
  if (lakh > 0) {
    result += `${lakh} lakh `;
  }
  if (remaining > 0) {
    result += `and ${remaining} `;
  }

  return result.trim();
};

const getApartmentListLink = (price: number) => {
  if (price < 10000000) {
    return {
      url: "/list/hyderabad/apartments-under-1cr",
      label: "See apartments under ₹1 crore in Hyderabad",
    };
  } else if (price >= 10000000 && price < 20000000) {
    return {
      url: "/list/hyderabad/apartments-1-2cr",
      label: "See apartments between ₹1-2 crore in Hyderabad",
    };
  } else if (price >= 20000000 && price < 40000000) {
    return {
      url: "/list/hyderabad/apartments-2-4cr",
      label: "See apartments between ₹2-4 crore in Hyderabad",
    };
  } else if (price >= 40000000) {
    return {
      url: "/list/hyderabad/ultra-luxury-apartments",
      label: "See ultra luxury apartments in Hyderabad",
    };
  }
  return null;
};

export default function ApartmentCalculator() {
  const [apartmentPrice, setApartmentPrice] = useState(15000000);
  const [squareFeet, setSquareFeet] = useState(2000);
  const [downPayment, setDownPayment] = useState(3000000);
  const [mortgageTerm, setMortgageTerm] = useState(20);
  const [interestRate, setInterestRate] = useState(8.5);
  const [isMortgage, setIsMortgage] = useState(true);
  const [interiorType, setInteriorType] = useState<Interior>(Interior.BASIC);
  const [maintanance, setMaintanance] = useState(0);

  const [emi, setEmi] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [loanToValueRatio, setLoanToValueRatio] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [pricePerSquareFoot, setPricePerSquareFoot] = useState(0);
  const [registrationCost, setRegistrationCost] = useState(0);
  const [stampDuty, setStampDuty] = useState(0);
  const [modt, setModt] = useState(0);
  const [cashToClose, setCashToClose] = useState(0);
  const [totalMonthlyPayment, setTotalMonthlyPayment] = useState(0);

  useEffect(() => {
    setStampDuty(truncateDecimals(apartmentPrice * 0.07, 0));
    const registration = truncateDecimals(apartmentPrice * 0.005, 0);
    setRegistrationCost(registration);

    setMaintanance(squareFeet * 3);
    if (isMortgage) {
      const loanAmount = Math.max(0, apartmentPrice - downPayment);
      setModt(isMortgage ? Math.min(50000, loanAmount * 0.05) : 0);
      const monthlyInterestRate = interestRate / 100 / 12;
      const numberOfPayments = mortgageTerm * 12;

      const monthlyMortgage =
        (loanAmount *
          monthlyInterestRate *
          Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
        (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);

      setEmi(monthlyMortgage);

      setLoanToValueRatio((loanAmount / apartmentPrice) * 100);

      const totalInterestValue =
        monthlyMortgage * numberOfPayments - loanAmount;
      setTotalInterest(totalInterestValue);
    } else {
      setModt(0);
      setEmi(0);
      setLoanToValueRatio(0);
      setTotalInterest(0);
    }
  }, [
    apartmentPrice,
    squareFeet,
    downPayment,
    mortgageTerm,
    interestRate,
    isMortgage,
    interiorType,
  ]);

  // total cost
  useEffect(() => {
    const interiorCost = interiorCosts[interiorType] * squareFeet;
    setTotalCost(
      apartmentPrice +
        interiorCost +
        registrationCost +
        stampDuty +
        modt +
        totalInterest
    );
  }, [
    registrationCost,
    stampDuty,
    modt,
    squareFeet,
    apartmentPrice,
    interiorType,
    totalInterest,
  ]);

  // cash to close
  useEffect(() => {
    if (isMortgage) {
      setCashToClose(downPayment + modt + registrationCost + stampDuty);
    } else {
      setCashToClose(apartmentPrice + registrationCost + stampDuty);
    }
  }, [
    totalCost,
    isMortgage,
    apartmentPrice,
    downPayment,
    modt,
    registrationCost,
    stampDuty,
  ]);

  // totalMonthly costs
  useEffect(() => {
    setTotalMonthlyPayment(emi + maintanance);
  }, [emi, maintanance]);

  return (
    <div className="bg-white rounded-lg shadow-lg my-4">
      <div className="bg-linear-to-r from-blue-50 to-indigo-50 p-4 rounded-t-lg mb-6">
        <h1 className="text-3xl font-bold mb-2">
          Apartment Purchase Cost Calculator
        </h1>
        <p className="text-gray-600">
          Calculate the cost of buying an apartment in Telangana
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
        <div className="space-y-6">
          <div>
            <label
              htmlFor="apartmentPrice"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Apartment Price (₹)
            </label>
            <input
              id="apartmentPrice"
              type="text"
              value={apartmentPrice.toLocaleString("en-IN")}
              onChange={(e) =>
                setApartmentPrice(Number(e.target.value.replace(/,/g, "")))
              }
              className="input mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200 focus:ring-opacity-50"
            />
            <p className="ml-2 mt-2 text-xs opacity-50">
              {numberToCroresLakhs(apartmentPrice)}
            </p>
          </div>
          <div>
            <label
              htmlFor="squareFeet"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Size of the apartment (Square Feet)
            </label>
            <input
              id="squareFeet"
              type="number"
              value={squareFeet}
              onChange={(e) => setSquareFeet(Number(e.target.value))}
              className="input mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200 focus:ring-opacity-50 no-spinners"
            />
          </div>
          <div>
            <div className="flex flex-row items-center gap-2">
              <label
                htmlFor="interiorType"
                className="block text-sm font-medium text-gray-700"
              >
                Interior Type
              </label>
              <a
                href="#interior-costing-guide"
                className="tooltip tooltip-right"
                data-tip="Select the type of interior finish for your apartment. "
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("interior-costing-guide")
                    ?.scrollIntoView({
                      behavior: "smooth",
                    });
                }}
              >
                <Info size={16} className="self-center" />
              </a>
            </div>
            <p className="text-xs opacity-50">
              When purchasing directly from builders, apartments do not come
              equipped with a kitchen or wardrobes. Kitchen interiors and
              wardrobes will have to be installed before the apartment is ready
              to occupy.
            </p>
            <select
              id="interiorType"
              value={interiorType}
              onChange={(e) => setInteriorType(e.target.value as any)}
              className="input mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200 focus:ring-opacity-50 no-spinners"
            >
              <option value={Interior.NONE}>None</option>
              <option value={Interior.BASIC}>Standard</option>
              <option value={Interior.PREMIUM}>Premium</option>
              <option value={Interior.LUXURY}>Luxury</option>
              {/* <option value={Interior.ULTRA_LUXURY}>Ultra Luxury</option> */}
            </select>
          </div>
          <div className="flex items-center">
            <input
              id="isMortgage"
              type="checkbox"
              checked={isMortgage}
              onChange={(e) => setIsMortgage(e.target.checked)}
              className="checkbox h-6 w-6 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-sm no-spinners"
            />
            <label
              htmlFor="isMortgage"
              className="ml-2 block text-sm text-gray-900"
            >
              I'm taking a home loan
            </label>
          </div>
          {isMortgage && (
            <div className="border-2 rounded-lg p-2 flex flex-col gap-6">
              <div>
                <label
                  htmlFor="downPayment"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Down Payment (₹)
                </label>
                <input
                  id="downPayment"
                  type="text"
                  value={downPayment.toLocaleString("en-IN")}
                  onChange={(e) =>
                    setDownPayment(Number(e.target.value.replace(/,/g, "")))
                  }
                  className="input mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200 focus:ring-opacity-50 no-spinners"
                />
                <p className="ml-2 mt-2 text-xs opacity-50">
                  {numberToCroresLakhs(downPayment)}
                </p>
              </div>
              <div>
                <label
                  htmlFor="mortgageTerm"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Mortgage Term (years)
                </label>
                <input
                  id="mortgageTerm"
                  type="range"
                  min="1"
                  max="30"
                  value={mortgageTerm}
                  onChange={(e) => setMortgageTerm(Number(e.target.value))}
                  className="range w-full range-xs"
                />
                <span className="text-sm text-gray-500">
                  {mortgageTerm} years
                </span>
              </div>
              <div>
                <label
                  htmlFor="interestRate"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Interest Rate (%)
                </label>
                <input
                  id="interestRate"
                  type="range"
                  step="0.1"
                  min="7"
                  max="14"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="range mt-1 block w-full range-xs"
                />
                <span className="text-sm text-gray-500">{interestRate} %</span>
              </div>
            </div>
          )}
        </div>
        <div className="space-y-6 md:border-l md:pl-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Results</h3>
            <div className="flex  flex-col bg-base-200 p-2 rounded-md gap-4">
              <div className="flex justify-between text-gray-700">
                <span className="text-gray-500">Apartment Price:</span>
                <span className="font-medium">
                  {formatCurrency(apartmentPrice)}
                </span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span className="text-gray-500">Cost of Interiors:</span>
                <span className="font-semibold">
                  {formatCurrency(interiorCosts[interiorType] * squareFeet)}
                </span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span className="text-gray-500">Registration Cost:</span>
                <span className="font-semibold">
                  {formatCurrency(registrationCost)}
                </span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span className="text-gray-500">Stamp duty:</span>
                <span className="font-semibold">
                  {formatCurrency(stampDuty)}
                </span>
              </div>
              {isMortgage && (
                <div className="flex justify-between text-gray-700">
                  <span className="text-gray-500">
                    Total Interest paid over {mortgageTerm} years:
                  </span>
                  <span className="font-semibold">
                    {formatCurrency(totalInterest)}
                  </span>
                </div>
              )}
              {isMortgage && (
                <div className="flex justify-between text-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">MODT:</span>
                    <div
                      className="tooltip tooltip-right flex items-center"
                      data-tip="Memorandum of Deposit of Title Deed. Lesser of 0.5% of loan amount or ₹50,000"
                    >
                      <Info size={18} />
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(modt)}</span>
                </div>
              )}
              <div className="pt-2 mt-2 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 font-semibold">
                    Total Cost:
                  </span>
                  <span className="font-bold text-lg">
                    {formatCurrency(totalCost)}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-2 collapse collapse-plus bg-base-100 text-2xl">
              <input type="checkbox" />
              <div className="collapse-title font-semibold">
                <h4 className="text-sm font-medium opacity-50">
                  Monthly payments
                </h4>
                <p className="text-2xl font-bold">
                  {formatCurrency(totalMonthlyPayment)}
                </p>
              </div>
              <div className="collapse-content text-sm">
                {isMortgage && (
                  <div className="flex flex-row justify-between text-sm opacity-50">
                    <p>EMI</p>
                    <p className="font-semibold">{formatCurrency(emi)}</p>
                  </div>
                )}
                <div className="flex flex-row justify-between text-sm opacity-50">
                  <p>Maintanance</p>
                  <p className="font-semibold">{formatCurrency(maintanance)}</p>
                </div>
              </div>
            </div>

            <div className="mt-2 collapse collapse-plus bg-base-100 text-2xl">
              <input type="checkbox" />
              <div className="collapse-title font-semibold">
                <h4 className="text-sm font-medium opacity-50">
                  Amount to complete the purchase
                </h4>
                <p className="text-2xl font-bold">
                  {formatCurrency(cashToClose)}
                </p>
              </div>
              <div className="collapse-content">
                {isMortgage && (
                  <div className="flex flex-row justify-between text-sm opacity-50">
                    <p>Downpayment</p>
                    <p>{formatCurrency(downPayment)}</p>
                  </div>
                )}
                {!isMortgage && (
                  <div className="flex flex-row justify-between text-sm opacity-50">
                    <p>Apartment price</p>
                    <p>{formatCurrency(apartmentPrice)}</p>
                  </div>
                )}

                <div className="flex flex-row justify-between text-sm opacity-50">
                  <p>Registration charges</p>
                  <p>{formatCurrency(registrationCost)}</p>
                </div>
                <div className="flex flex-row justify-between text-sm opacity-50">
                  <p>Stamp duty</p>
                  <p>{formatCurrency(stampDuty)}</p>
                </div>
              </div>
            </div>
            {/* Apartment list link based on price, as a button below all results */}
            <div className="space-y-3">
              {(() => {
                const link = getApartmentListLink(apartmentPrice);
                if (!link) return null;
                return (
                  <Link
                    href={link.url}
                    className="btn btn-primary w-full md:w-auto p-8"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                  </Link>
                );
              })()}
              
              {/* Custom interior quote button */}
              <a
                href="https://www.instagram.com/_narchitects_/"
                className="btn btn-outline btn-secondary w-full p-8"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get a custom interiors quote
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
